<script lang="ts" setup>
import Mousetrap from 'mousetrap';
import { computed, onMounted, onUnmounted, shallowRef, watch } from 'vue';
import { storeToRefs } from 'pinia';
import LockScreenBtn from './Control/LockScreenBtn.vue';
import useNavigationStore from '../stores/navigation';
import useLocale from '../composables/useLocale';
import useUiStore from '../stores/ui';
import useReaderStore from '../stores/reader';
import AddBookmarkDialog from './bookmarks/AddBookmarkDialog.vue';
import { useDisplay } from 'vuetify';
import { FontAwesomeIcon, FontAwesomeLayers } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faBarsStaggered,
  faChevronLeft,
  faClipboardList,
  faHighlighterLine,
  faList,
} from '@fortawesome/pro-solid-svg-icons';
import { faCircleXmark, faClipboardList as faClipboardListRegular } from '@fortawesome/pro-regular-svg-icons';
import { useElementSize } from '@vueuse/core';
import SwitchModeBtn from './Control/SwitchModeBtn.vue';
import { SwitchBookPlayerModeEvent } from '../types';

library.add([
  faBarsStaggered,
  faChevronLeft,
  faList,
  faHighlighterLine,
  faClipboardList,
  faClipboardListRegular,
  faCircleXmark,
]);

type Emits = {
  (e: 'close'): void;
  (e: 'switchBookPlayerMode', event: SwitchBookPlayerModeEvent): void;
};
const emit = defineEmits<Emits>();

type Props = {
  showTooltips?: boolean;
  profileId?: string;
};
withDefaults(defineProps<Props>(), {
  showTooltips: true,
});

const { t } = useLocale();
const uiStore = useUiStore();
const {
  isTableOfContentVisible,
  isControlsVisible,
  isAnnotationsDrawerVisible,
  isTopBarTransparent,
  isMobileLandscape,
} = storeToRefs(uiStore);
const {
  toggleAnnotationsDrawer,
  toggleTableOfContentDrawer,
  closeTableOfContentDrawer,
  closeLanguageMenu,
  toggleDrawerAndControls,
  hideControlsAfterTimeout,
  toggleVisibleVolumeSlider,
  setTopBarHeight,
} = uiStore;
const { isTocTop, isBookmarkBtnsToolbar, hasBookmarks, bookId } = storeToRefs(useReaderStore());
const { hasTableOfContents, hasAnnotations } = storeToRefs(useNavigationStore());

const closePlayer = async () => {
  emit('close');
};

onMounted(() => {
  Mousetrap.bind('esc', () => {
    (document.activeElement as HTMLElement)?.focus();
    closeTableOfContentDrawer();
    toggleAnnotationsDrawer(false);
    closeLanguageMenu();
    toggleVisibleVolumeSlider(false);
    return false;
  });
});

onUnmounted(() => {
  Mousetrap.unbind('esc');
});

const { platform, xs } = useDisplay();
const isTouch = computed(() => platform.value.touch);

const topBarRef = shallowRef<HTMLElement | null>(null);
const { height: topBarHeight } = useElementSize(topBarRef);

const topHeight = computed(() => `${topBarHeight.value}px`);
const topOffset = computed(() =>
  isControlsVisible.value ? '0' : `calc(-${topBarHeight.value}px - env(safe-area-inset-top, 0px))`,
);

watch(topBarHeight, () => {
  setTopBarHeight(topBarHeight.value);
});
</script>

<template>
  <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions vuejs-accessibility/mouse-events-have-key-events -->
  <div
    v-show="!isControlsVisible"
    class="toolbar-surface order-3 toolbar-shadow"
    :class="{
      'pointer-events-none': isTopBarTransparent,
    }"
    @mouseenter="
      () => {
        if (!isTouch) {
          toggleDrawerAndControls(false, true);
        }
      }
    "
    @touchstart.passive="
      () => {
        toggleDrawerAndControls(false, true);
        hideControlsAfterTimeout();
      }
    "
  />
  <!-- eslint-disable-next-line vuejs-accessibility/mouse-events-have-key-events -->
  <v-toolbar
    ref="topBarRef"
    data-testid="bp-toolbar"
    :color="isTopBarTransparent ? 'transparent' : 'brand'"
    class="toolbar-surface order-3 top-toolbar text-on-brand"
    :class="{
      'pointer-events-none': isTopBarTransparent,
    }"
    :tag="isTopBarTransparent ? 'div' : 'header'"
    @mouseenter="
      () => {
        if (!isTouch) {
          toggleDrawerAndControls(false, true);
        }
      }
    "
    @touchstart.passive="() => toggleDrawerAndControls(false, true)"
    @mouseleave="hideControlsAfterTimeout"
    @touchend="hideControlsAfterTimeout"
    @focusin.passive="() => toggleDrawerAndControls(false, true)"
    @focusout="hideControlsAfterTimeout"
    :density="isMobileLandscape ? 'compact' : 'default'"
  >
    <template #prepend>
      <v-sheet class="bg-transparent mt-1 ms-1 me-auto">
        <v-btn
          v-if="isTocTop && hasTableOfContents"
          id="tableOfContents"
          data-testid="bp-toolbar-toc-btn"
          class="ml-0"
          icon
          color="on-brand"
          :title="t('title_table_of_contents')"
          variant="text"
          :ripple="false"
          :active="isTableOfContentVisible"
          @click="toggleTableOfContentDrawer(!isTableOfContentVisible)"
          @keydown.esc="closeTableOfContentDrawer"
          size="48"
        >
          <FontAwesomeIcon v-if="!isTableOfContentVisible" size="xl" icon="fa-solid fa-list" flip="horizontal" />
          <FontAwesomeLayers v-else class="ml-n2">
            <FontAwesomeIcon size="xl" icon="fa-solid fa-bars-staggered" flip="horizontal" />
            <FontAwesomeIcon size="lg" icon="fa-solid fa-chevron-left" transform="shrink-3 right-16" />
          </FontAwesomeLayers>
          <v-tooltip
            :disabled="!showTooltips"
            activator="parent"
            location="bottom"
            :text="t('title_table_of_contents')"
          />
        </v-btn>
      </v-sheet>
      <SwitchModeBtn
        v-if="xs && isTopBarTransparent"
        class="pointer-events-auto"
        :show-tooltips="showTooltips"
        :btn-size="48"
        :icon-size="'xl'"
        @switch-book-player-mode="emit('switchBookPlayerMode', $event)"
      />
    </template>
    <v-spacer></v-spacer>
    <template #append>
      <LockScreenBtn :show-tooltips="showTooltips" />
      <slot name="toolbar-actions"></slot>
      <v-sheet class="bg-transparent mt-1 me-1 ms-auto d-flex align-center">
        <AddBookmarkDialog
          v-if="hasBookmarks && isBookmarkBtnsToolbar && bookId"
          :book-id="bookId"
          :show-tooltips="showTooltips"
        />
        <v-btn
          v-if="hasAnnotations"
          id="annotationsDrawerIcon"
          data-testid="bp-annotations-drawer-btn"
          color="on-brand"
          variant="text"
          :ripple="false"
          icon
          :title="t('book_player.label_annotations')"
          @click.stop="toggleAnnotationsDrawer(!isAnnotationsDrawerVisible)"
          :active="isAnnotationsDrawerVisible"
          size="48"
        >
          <FontAwesomeIcon
            size="xl"
            :icon="isAnnotationsDrawerVisible ? 'fa-solid fa-clipboard-list' : 'fa-regular fa-clipboard-list'"
          />
          <v-tooltip
            activator="parent"
            location="bottom"
            :text="t('book_player.label_annotations')"
            :disabled="!showTooltips"
          />
        </v-btn>
        <slot
          name="close-btn"
          :close-player="closePlayer"
          :display-none="isTableOfContentVisible && isTopBarTransparent"
        >
          <v-btn
            :class="{
              'pointer-events-auto': isTopBarTransparent,
            }"
            variant="text"
            color="on-brand"
            icon
            :ripple="false"
            data-testid="bp-close-player-btn"
            :aria-label="t('button_close_app')"
            @click.stop="closePlayer"
            size="48"
          >
            <FontAwesomeIcon size="xl" icon="fa-regular fa-circle-xmark" />
            <v-tooltip activator="parent" location="bottom" :text="t('button_close_app')" :disabled="!showTooltips" />
          </v-btn>
        </slot>
      </v-sheet>
    </template>
  </v-toolbar>
</template>

<style scoped lang="scss">
@use 'vuetify/settings' as v;

.toolbar-opaque {
  opacity: 0.95;
}

.toolbar-surface {
  z-index: 999;
  width: 100vw;
  position: fixed;
  padding-top: env(safe-area-inset-top, 0px);
}

.pointer-events-auto {
  pointer-events: auto;
}

.pointer-events-none {
  pointer-events: none;
}

.top-toolbar {
  top: v-bind(topOffset) !important;
}

.toolbar-shadow {
  top: 0;
  height: v-bind(topHeight);
  padding-top: env(safe-area-inset-top, 0px) !important;
}

:deep(.v-sheet) {
  background-color: transparent;
}
</style>
